// "Crossfire" — a 96x96 two-player map with 180-degree rotational symmetry.
// Each player owns a high-ground main behind a single ramp, a low-ground
// natural expansion, and both contest a centre plateau reachable by two ramps.
//
// Mineral lines are hand-authored as offsets from the town hall so that the
// average haul distance lands on the ~8 tile figure the economy tuning in
// constants.js is derived from.

import { Grid } from './grid.js';
import { MAP_W, MAP_H } from './constants.js';

/** Rotate a footprint anchor 180 degrees about the map centre. */
const rotA = (x, y, w, h) => ({ x: MAP_W - x - w, y: MAP_H - y - h });
/** Rotate a point 180 degrees about the map centre. */
const rotP = (x, y) => ({ x: MAP_W - x, y: MAP_H - y });

function rect(grid, x0, y0, x1, y1, fn) {
  for (let y = Math.max(0, y0); y <= Math.min(grid.h - 1, y1); y++)
    for (let x = Math.max(0, x0); x <= Math.min(grid.w - 1, x1); x++)
      fn(y * grid.w + x, x, y);
}

// Mineral patch anchors as (dx,dy) offsets from the town-hall centre tile.
// MEASURED, not guessed. economy.js asked for these arcs to be pushed ~1 tile
// further out so every patch could be mined from contact instead of relying on
// its stand-off workaround for the three tightest patches. I tried it: pushing
// the arc out moves the outer patches far enough that a 12-worker line drops
// from 91% of linear to 83%, because the extra travel costs more than the
// workaround does. The tight arc wins on the curve that actually matters, so
// the stand-off in economy.js stays. See README "known gaps".
const MAIN_PATCHES = [
  [-8, -3], [-8, -1], [-8, 1], [-7, 3],
  [-6, -5], [-4, -7], [-1, -8], [2, -8],
];
const NAT_PATCHES = [
  [6, -6], [8, -3], [8, 0], [7, 3], [4, 6], [0, 8],
];

const BASE_LAYOUT = [
  { cc: { x: 20, y: 76 }, patches: MAIN_PATCHES, geyser: { x: 27, y: 68 }, isMain: true },
  { cc: { x: 46, y: 84 }, patches: NAT_PATCHES,  geyser: { x: 53, y: 89 }, isMain: false },
];

export function buildMap() {
  const grid = new Grid(MAP_W, MAP_H);
  const nodes = [];
  const bases = [];

  // ---- terrain -----------------------------------------------------------
  const plateaus = [
    [6, 62, 37, 89],   // player 0 main
    [59, 7, 90, 34],   // player 1 main (180-degree rotation of the above)
    [42, 42, 54, 54],  // contested centre, self-symmetric about the map centre
  ];
  for (const [x0, y0, x1, y1] of plateaus) rect(grid, x0, y0, x1, y1, (i) => { grid.height[i] = 1; });

  const ramps = [
    [38, 71, 41, 77],  // player 0 main ramp, east face
    [55, 19, 58, 25],  // player 1 main ramp, west face
    [45, 39, 51, 41],  // centre plateau, north ramp
    [45, 55, 51, 57],  // centre plateau, south ramp
  ];
  for (const [x0, y0, x1, y1] of ramps) rect(grid, x0, y0, x1, y1, (i) => { grid.ramp[i] = 1; grid.height[i] = 0; });

  // Hard blockers that narrow the open lanes into real chokes.
  const blockers = [[24, 22, 33, 31], [63, 65, 72, 74], [8, 18, 15, 28], [81, 68, 88, 78]];
  for (const [x0, y0, x1, y1] of blockers) rect(grid, x0, y0, x1, y1, (i) => { grid.cliff[i] = 2; });

  grid.bakeCliffs();

  // ---- bases + resources -------------------------------------------------
  for (let owner = 0; owner < 2; owner++) {
    for (const L of BASE_LAYOUT) {
      const cc = owner === 0 ? { ...L.cc } : rotP(L.cc.x, L.cc.y);
      const baseIdx = bases.length;
      bases.push({ owner, cc, isMain: L.isMain, index: baseIdx });

      for (const [dx, dy] of L.patches) {
        const ax = L.cc.x + dx, ay = L.cc.y + dy;
        const p = owner === 0 ? { x: ax, y: ay } : rotA(ax, ay, 2, 1);
        nodes.push({ type: 'mineral', x: p.x, y: p.y, w: 2, h: 1, base: baseIdx });
      }
      const g = owner === 0 ? { ...L.geyser } : rotA(L.geyser.x, L.geyser.y, 3, 3);
      nodes.push({ type: 'geyser', x: g.x, y: g.y, w: 3, h: 3, base: baseIdx });
    }
  }

  validate(grid, nodes, bases);
  return { grid, nodes, bases, startLocations: bases.filter((b) => b.isMain).map((b) => b.cc) };
}

/** Fail loudly at boot if the authored map is geometrically broken or unfair. */
function validate(grid, nodes, bases) {
  const errs = [];

  // Fairness: the map must be exactly symmetric under 180-degree rotation about
  // the centre point, for terrain height, walkability AND resource placement.
  // Anything less hands one player an advantage that no amount of AI tuning can
  // compensate for, and it is invisible unless asserted.
  for (let y = 1; y < grid.h; y++) {
    for (let x = 1; x < grid.w; x++) {
      const a = y * grid.w + x, b = (grid.h - y) * grid.w + (grid.w - x);
      if (grid.height[a] !== grid.height[b]) { errs.push(`height asymmetry at ${x},${y}`); y = grid.h; break; }
      if (!!grid.cliff[a] !== !!grid.cliff[b]) { errs.push(`walkability asymmetry at ${x},${y}`); y = grid.h; break; }
      if (!!grid.ramp[a] !== !!grid.ramp[b]) { errs.push(`ramp asymmetry at ${x},${y}`); y = grid.h; break; }
    }
  }
  const claimed = new Map();
  for (const n of nodes) {
    for (let j = n.y; j < n.y + n.h; j++) {
      for (let i = n.x; i < n.x + n.w; i++) {
        if (!grid.terrainPassable(i, j)) errs.push(`${n.type} @${n.x},${n.y} covers unwalkable ${i},${j}`);
        if (grid.ramp[j * grid.w + i]) errs.push(`${n.type} @${n.x},${n.y} sits on a ramp`);
        const k = `${i},${j}`;
        if (claimed.has(k)) errs.push(`resource overlap at ${k}`);
        claimed.set(k, n);
      }
    }
  }
  for (const b of bases) {
    const ax = b.cc.x - 2, ay = b.cc.y - 2;
    for (let j = ay; j < ay + 4; j++)
      for (let i = ax; i < ax + 4; i++) {
        if (!grid.terrainPassable(i, j)) errs.push(`base ${b.index} town hall covers unwalkable ${i},${j}`);
        if (claimed.has(`${i},${j}`)) errs.push(`base ${b.index} town hall overlaps a resource at ${i},${j}`);
      }
  }
  if (errs.length) throw new Error('map validation failed:\n  ' + errs.join('\n  '));
}
